
import React, { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Shield, UserPlus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast"

interface AdminWithUserInfo {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  user_name?: string;
  user_email?: string;
}

export default function AdminSettings() {
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);

  const { data: admins, isLoading, refetch } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async (): Promise<AdminWithUserInfo[]> => {
      // First get all admin roles
      const { data: adminRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('role', 'admin');

      if (rolesError) {
        console.error('Error fetching admin roles:', rolesError);
        return [];
      }

      if (!adminRoles || adminRoles.length === 0) {
        return [];
      }

      // Then get user info for each admin
      const adminsWithUserInfo: AdminWithUserInfo[] = [];
      
      for (const adminRole of adminRoles) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('name, email')
          .eq('id', adminRole.user_id)
          .single();

        adminsWithUserInfo.push({
          id: adminRole.id,
          user_id: adminRole.user_id,
          role: adminRole.role,
          created_at: adminRole.created_at,
          user_name: userData?.name || 'Não informado',
          user_email: userData?.email || 'Não informado'
        });
      }

      return adminsWithUserInfo;
    }
  });

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) {
      toast.error("Por favor, insira um email válido");
      return;
    }

    setIsAddingAdmin(true);

    try {
      // First, check if user exists
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('email', newAdminEmail.trim())
        .single();

      if (userError || !userData) {
        toast.error("Usuário não encontrado. O usuário deve estar cadastrado no sistema.");
        return;
      }

      // Check if user is already admin
      const { data: existingRole, error: roleCheckError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', userData.id)
        .eq('role', 'admin')
        .single();

      if (existingRole) {
        toast.error("Este usuário já é um administrador");
        return;
      }

      // Add admin role
      const { error: addRoleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userData.id,
          role: 'admin'
        });

      if (addRoleError) {
        console.error('Error adding admin role:', addRoleError);
        toast.error("Erro ao adicionar administrador");
        return;
      }

      toast.success("Administrador adicionado com sucesso!");
      setNewAdminEmail("");
      refetch();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast.error("Erro ao adicionar administrador");
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    if (window.confirm("Tem certeza que deseja remover este administrador?")) {
      try {
        const { error } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', userId)
          .eq('role', 'admin');

        if (error) {
          console.error('Error removing admin:', error);
          toast.error("Erro ao remover administrador");
          return;
        }

        toast.success("Administrador removido com sucesso!");
        refetch();
      } catch (error) {
        console.error('Error removing admin:', error);
        toast.error("Erro ao remover administrador");
      }
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Configurações"
          subtitle="Gerencie administradores e configurações do sistema."
          backTo="/admin/control-center"
        />

        {/* Add New Admin */}
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus size={20} />
              Adicionar Administrador
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input
                placeholder="Email do usuário"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleAddAdmin}
                disabled={isAddingAdmin}
                className="flex items-center gap-2"
              >
                <Plus size={16} />
                {isAddingAdmin ? "Adicionando..." : "Adicionar"}
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              O usuário deve estar cadastrado no sistema para ser promovido a administrador.
            </p>
          </CardContent>
        </Card>

        {/* Current Admins */}
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield size={20} />
              Administradores Atuais
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="text-gray-500">Carregando administradores...</div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Função</TableHead>
                    <TableHead>Adicionado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins?.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell>{admin.user_name}</TableCell>
                      <TableCell>{admin.user_email}</TableCell>
                      <TableCell>
                        <Badge className="bg-blue-100 text-blue-800">
                          Administrador
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(admin.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAdmin(admin.user_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* System Settings */}
        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle>Configurações do Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-sm text-gray-500">
                Configurações adicionais do sistema serão implementadas aqui.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
